import { useCurrency } from "@/hooks/use-currency";
import { cn } from "@/lib/utils";

type Size = "hero" | "card" | "inline";

interface Props {
  /** Required: price in EUR cents (e.g. 1900 = €19.00). */
  eurCents: number;
  size?: Size;
  /** Show a small "from" prefix before the amount. */
  fromPrefix?: boolean;
  /** Optional suffix like "/ mo", "/ cohort", "/ hr". */
  suffix?: string;
  className?: string;
}

const SIZES: Record<Size, { base: string; local: string; sep: string }> = {
  hero: {
    base: "font-display text-4xl font-bold tabular-nums md:text-5xl",
    local: "text-base text-muted-foreground",
    sep: "block mt-1",
  },
  card: {
    base: "font-display text-3xl font-bold tabular-nums",
    local: "text-sm text-muted-foreground",
    sep: "block mt-1",
  },
  inline: {
    base: "font-semibold tabular-nums",
    local: "text-xs text-muted-foreground",
    sep: "ml-1",
  },
};

/**
 * Single source of truth for price rendering. Always shows
 * `formatBase(eur) · formatLocal(eur)` from useCurrency(), with a single
 * geo-correct local currency (never both XOF and XAF; never GNF unless GN).
 */
export function Price({ eurCents, size = "card", fromPrefix, suffix, className }: Props) {
  const { baseCurrency, localCurrency, formatBase, formatLocal } = useCurrency();
  const eurMajor = (Number(eurCents) || 0) / 100;
  const showLocal = localCurrency !== baseCurrency;
  const s = SIZES[size];

  return (
    <span
      className={cn("inline-flex flex-wrap items-baseline gap-x-2", className)}
      data-price
      data-eur-cents={eurCents}
    >
      {fromPrefix && (
        <span className="text-sm text-muted-foreground">from</span>
      )}
      <span className={s.base}>{formatBase(eurMajor)}</span>
      {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
      {showLocal && (
        <span className={cn(s.local, s.sep)} aria-label="approximate local price">
          · {formatLocal(eurMajor)}
        </span>
      )}
    </span>
  );
}